/* ==========================================================================
   JADUZAR INK — variant selection

   Keeps the hidden `id` field in step with the chosen options, and mirrors
   price and availability. Without this the form posts whichever variant the
   server rendered first, which is how the old page charged studio prices for
   anesthesia bookings.
   ========================================================================== */
(() => {
  /**
   * Format a Shopify price the same way the Netherlands panel does.
   *
   * The amount already arrives in the active market's presentment currency —
   * Liquid renders product.variants in whatever currency Shopify is serving —
   * so nothing is converted here. Only the presentation is applied, from the
   * currency's ISO code, which is why EUR, GBP and USD each get their own
   * symbol without any of them being written down.
   *
   * Previously this read `shop.money_without_trailing_zeros_format`, which is
   * not a Shopify property. It resolved to an empty string, the fallback
   * dropped the format entirely, and every Bali price rendered as a bare
   * number such as "500".
   */
  function money(cents, currency, locale, format) {
    if (currency) {
      try {
        return new Intl.NumberFormat(locale || undefined, {
          style: 'currency',
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(cents / 100);
      } catch (e) {
        /* fall through to the shop's own format string */
      }
    }

    const amount = (cents / 100).toLocaleString(locale || 'en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return format ? format.replace(/\{\{\s*\w+\s*\}\}/, amount) : amount;
  }

  function setup(root) {
    let variants;
    try {
      variants = JSON.parse(root.dataset.variants);
    } catch {
      return;
    }
    if (!Array.isArray(variants) || !variants.length) return;

    const form = root.closest('form');
    if (!form) return;

    const idField = form.querySelector('[data-ji-variant-id]');
    const submit = form.querySelector('[data-ji-submit]');
    // Every price that mirrors the variant, not just the first one: the
    // headline figure and the summary row must never disagree.
    const priceEls = document.querySelectorAll('[data-ji-price]');
    const format = root.dataset.moneyFormat || null;
    const currency = root.dataset.jiCurrency || '';
    const locale = root.dataset.jiLocale || '';

    const labels = {
      book: submit ? submit.dataset.bookLabel || '' : '',
      sold: submit ? submit.dataset.soldLabel || '' : '',
    };

    // Expose this form's money formatter. The deposit amount comes from
    // Liquid and the variant amount is formatted here; running both through
    // the same function is what stops "€1.000" and "€2,500" appearing on the
    // same panel. Nothing depends on it — a caller that does not find it
    // falls back to the Liquid string.
    form.jiMoney = (cents) => money(cents, currency, locale, format);

    // Broadcast the selected variant. Nothing in this file acts on it; it
    // exists so features like the payment choice and the medical marker can
    // react without reaching into the variant logic and without duplicating
    // the matching.
    function announce(variant) {
      form.dispatchEvent(
        new CustomEvent('ji:variant', {
          bubbles: true,
          detail: { variant, root, form, submit },
        })
      );
    }

    function selected() {
      return Array.from(root.querySelectorAll('[data-ji-option]:checked'))
        .sort((a, b) => a.dataset.jiOption - b.dataset.jiOption)
        .map((input) => input.value);
    }

    function update() {
      const chosen = selected();
      const match = variants.find((v) =>
        chosen.every((value, i) => v.options[i] === value)
      );

      if (!match) {
        if (submit) {
          submit.disabled = true;
          submit.textContent = labels.sold;
        }
        announce(null);
        return;
      }

      if (idField) idField.value = match.id;

      if (priceEls.length && typeof match.price === 'number') {
        const formatted = money(match.price, currency, locale, format);
        priceEls.forEach((el) => {
          el.textContent = formatted;
        });
      }

      if (submit) {
        submit.disabled = !match.available;
        submit.textContent = match.available ? labels.book : labels.sold;
      }

      // Last thing in the update, so anything listening runs after price,
      // availability and the hidden id have all settled. The payment choice
      // relies on that order: it reads the price element this update has
      // just written.
      announce(match);
    }

    root.addEventListener('change', (e) => {
      if (e.target.matches('[data-ji-option]')) update();
    });

    update();
  }

  function init() {
    document.querySelectorAll('[data-ji-variants]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
